import type { CertificateTemplateResponse } from '../../data-access/api/responses.js';
import { parseDateForPicker } from '../../data-access/dates.js';
import type { CaptivePolicy } from '../../data-access/models.js';
import { getDynamicFieldKeysForTemplate } from './dynamic-fields.js';
import { buildDefaultCertificatePolicyNumber } from './policy.js';
import type { DynamicFieldKey, FormValues, StorageLocationFormValue } from './types.js';

export const INITIAL_FORM_VALUES: FormValues = {
  certificateIssuedTo: '',
  customerAddress: '',
  countryCode: '',
  policyYear: '',
  certificateTemplateId: '',

  effectiveDate: null,
  contractingEntity: '',
  currency: 'USD',
  policyExpirationDate: null,
  policyNumber: '',

  propertyOnBoardLimit: 0,
  transitLimit: 0,
  atmLimit: 0,
  compusafeLimit: 0,
  moneyProcessingLimit: 0,

  storageLocations: [],

  situation: '',
  sanctionedCountryTouchpoint: false,
  additionalComments: '',
};

export const createEmptyStorageLocation = (index: number): StorageLocationFormValue => ({
  id: `storage-location-${Date.now()}-${index}`,
  storageLocation: '',
  limitStorage: 0,
});

export const getPolicyDefaults = ({
  policyYear,
  selectedPolicy,
}: {
  policyYear: string;
  selectedPolicy: CaptivePolicy | null;
}): Partial<FormValues> => ({
  currency: selectedPolicy?.totalLimitCurrency || 'USD',
  policyNumber: buildDefaultCertificatePolicyNumber(policyYear),
  effectiveDate: parseDateForPicker(selectedPolicy?.startDate),
  policyExpirationDate: parseDateForPicker(selectedPolicy?.endDate),
});

export const getDynamicFieldDefaults = (fieldKeys: DynamicFieldKey[]): Partial<FormValues> => {
  const defaults: Partial<FormValues> = {};

  for (const fieldKey of fieldKeys) {
    switch (fieldKey) {
      case 'propertyOnBoardLimit':
      case 'transitLimit':
      case 'atmLimit':
      case 'compusafeLimit':
      case 'moneyProcessingLimit':
        defaults[fieldKey] = 0;
        break;
      case 'storageLocations':
        defaults.storageLocations = [createEmptyStorageLocation(1)];
        break;
      case 'sanctionedCountryTouchpoint':
        defaults.sanctionedCountryTouchpoint = false;
        break;
      case 'situation':
      case 'additionalComments':
        defaults[fieldKey] = '';
        break;
    }
  }

  return defaults;
};

export const resetTemplateSelection = (currentValues: FormValues): FormValues => ({
  ...INITIAL_FORM_VALUES,
  certificateIssuedTo: currentValues.certificateIssuedTo,
  customerAddress: currentValues.customerAddress,
  countryCode: currentValues.countryCode,
  policyYear: currentValues.policyYear,
  storageLocations: [],
});

export const applyTemplateSelection = ({
  currentValues,
  template,
  selectedPolicy,
}: {
  currentValues: FormValues;
  template: CertificateTemplateResponse | null;
  selectedPolicy: CaptivePolicy | null;
}): FormValues => {
  const cleared = resetTemplateSelection(currentValues);

  return {
    ...cleared,
    ...getPolicyDefaults({ policyYear: cleared.policyYear, selectedPolicy }),
    ...getDynamicFieldDefaults(getDynamicFieldKeysForTemplate(template)),
    certificateTemplateId: template?.id ?? '',
  };
};

export const getInitialValues = (): FormValues => ({
  ...INITIAL_FORM_VALUES,
  storageLocations: [],
});
